<script setup>
import { computed, reactive, ref } from 'vue';

import { useAlert } from 'dashboard/composables';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import {
  providerFormContent,
  providerOptions,
  resolveResultMessage,
  resolveSaveToastMessage,
} from '../../helpers/kraftchatAIProviderConfig';
import KraftchatAIProviderCreateForm from './KraftchatAIProviderCreateForm.vue';
import KraftchatAIProviderList from './KraftchatAIProviderList.vue';

const props = defineProps({
  providerCredentials: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const isSavingNewProvider = ref(false);
const savingCredentialId = ref(null);
const deletingProviderId = ref(null);
const editingCredentialId = ref(null);

// Keyed by credential id. Session-only: never persisted, and intentionally
// lost on reload/unmount - the persisted `status`/`last_tested_at` columns
// (refreshed via `emit('saved')`) remain the source of truth across visits.
const credentialTestState = reactive({});

const setCredentialTestState = (id, patch) => {
  credentialTestState[id] = { ...credentialTestState[id], ...patch };
};

// Runs POST provider_credentials/:id/test - the single source of truth for
// validation. The frontend never re-implements this logic, it only renders
// whatever the backend concludes (see ValidationService/OpenaiProber).
const runCredentialTest = async id => {
  setCredentialTestState(id, { testing: true });
  try {
    const { data } = await KraftchatAIAPI.testProviderCredential(id);
    setCredentialTestState(id, { testing: false, result: data.payload });
    return data.payload;
  } catch (error) {
    setCredentialTestState(id, { testing: false });
    throw error;
  }
};

// Save success and validation success are different things - this always
// runs after a create/update resolves so the toast reflects whether the
// credential actually works, never just that the row was persisted. A manual
// "Testar novamente" (isUpdate undefined) is not a save, so it gets the
// plainer, operational-aware wording instead of the "conectada/atualizada"
// framing.
const testAndNotify = async (id, { isUpdate } = {}) => {
  const isSaveTriggered = isUpdate !== undefined;

  try {
    const result = await runCredentialTest(id);
    useAlert(
      isSaveTriggered
        ? resolveSaveToastMessage(result, { isUpdate })
        : resolveResultMessage(result)
    );
  } catch (error) {
    useAlert(providerFormContent.testFailedFallback);
  } finally {
    emit('saved');
  }
};

const connectedProviderValues = computed(() => {
  return props.providerCredentials.map(credential => credential.provider);
});

const availableProviderOptions = computed(() => {
  return providerOptions.filter(
    option => !connectedProviderValues.value.includes(option.value)
  );
});

const hasAvailableProviderOptions = computed(() => {
  return availableProviderOptions.value.length > 0;
});

const handleCreateProvider = async payload => {
  const apiKey = payload.api_key.trim();

  if (!apiKey) {
    useAlert(providerFormContent.apiKeyRequiredError);
    return;
  }

  isSavingNewProvider.value = true;

  let credentialId;
  try {
    const { data } = await KraftchatAIAPI.createProviderCredential({
      provider: payload.provider,
      default_model: payload.default_model,
      api_key: apiKey,
    });
    credentialId = data.payload.id;
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        providerFormContent.fallbackError
    );
    return;
  } finally {
    isSavingNewProvider.value = false;
  }

  emit('saved');
  await testAndNotify(credentialId, { isUpdate: false });
};

const handleUpdateProvider = async (credential, payload) => {
  const apiKey = payload.api_key.trim();

  savingCredentialId.value = credential.id;

  const requestPayload = { default_model: payload.default_model };
  if (apiKey) {
    requestPayload.api_key = apiKey;
  }

  try {
    await KraftchatAIAPI.updateProviderCredential(
      credential.id,
      requestPayload
    );
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        providerFormContent.fallbackError
    );
    return;
  } finally {
    savingCredentialId.value = null;
  }

  editingCredentialId.value = null;
  emit('saved');
  await testAndNotify(credential.id, { isUpdate: true });
};

const handleTestProvider = async credential => {
  await testAndNotify(credential.id);
};

const handleDeleteProvider = async credential => {
  deletingProviderId.value = credential.id;

  try {
    await KraftchatAIAPI.deleteProviderCredential(credential.id);
    useAlert(providerFormContent.deleteSuccessMessage);
    emit('saved');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        providerFormContent.deleteFallbackError
    );
  } finally {
    deletingProviderId.value = null;
  }
};
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="max-w-5xl space-y-6">
      <div class="space-y-1">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ providerFormContent.title }}
        </h2>

        <p class="text-sm leading-6 text-n-slate-11">
          {{ providerFormContent.description }}
        </p>
      </div>

      <KraftchatAIProviderCreateForm
        v-if="hasAvailableProviderOptions"
        :available-provider-options="availableProviderOptions"
        :is-saving="isSavingNewProvider"
        @create-provider="handleCreateProvider"
      />

      <p
        v-else
        class="rounded-xl border border-dashed border-n-weak bg-n-background p-4 text-sm text-n-slate-11"
      >
        {{ providerFormContent.noAvailableProviders }}
      </p>

      <KraftchatAIProviderList
        :provider-credentials="providerCredentials"
        :editing-credential-id="editingCredentialId"
        :saving-credential-id="savingCredentialId"
        :deleting-provider-id="deletingProviderId"
        :credential-test-state="credentialTestState"
        @edit-provider="editingCredentialId = $event.id"
        @delete-provider="handleDeleteProvider"
        @update-provider="handleUpdateProvider"
        @cancel-edit="editingCredentialId = null"
        @test-provider="handleTestProvider"
      />
    </div>
  </section>
</template>
